<script setup lang="ts">
type Props = {
  title?: string
  open?: boolean
  name?: string
  isToggleable?: boolean
  variant?: 'primary' | 'secondary' // primary: chevron (default), secondary: +- indicates open/close
  gaOptions?: Omit<DataLayerObject, 'eventName' | 'event'>
  // Lands on <summary> so tests can open a specific panel; the root div is not the click target.
  testId?: string
  // Lands on <details>, so tests can scope a query to one panel's contents.
  panelTestId?: string
  eventName?: DataLayerEventName // Optional, if you want to override the default event name
}

const props = withDefaults(defineProps<Props>(), { isToggleable: true, variant: 'primary' })
const emit = defineEmits<{
  (e: 'toggle', isOpen: boolean): void
}>()
const contentReady = ref(false)
const detailsRef = ref<HTMLDetailsElement | null>(null)
const isAccordionOpen = ref(props.open || false)
const needsPolyfill = ref(false)
// Only override `action` when the caller already tracks one; otherwise leave the key absent.
const accordionActionParam = (): { action?: 'open' | 'close' } =>
  props.gaOptions?.action ? { action: isAccordionOpen.value ? 'open' : 'close' } : {}
const accordionHeight = ref('auto')
const contentHeight = ref(0)
const isOpenOnMount = ref(props.open || false)

function isSafari16OrBelow() {
  const userAgent = navigator.userAgent
  const isSafari = /^((?!chrome|android|firefox).)*safari/i.test(userAgent)

  if (!isSafari) return false

  // Safari includes "Version/X.X.X" in its user agent
  const versionMatch = userAgent.match(/Version\/(\d+)\.(\d+)/)
  if (!versionMatch) return false

  const majorVersion = parseInt(versionMatch[1], 10)
  return majorVersion <= 16
}

// Manual control of details element for Safari
const toggleAccordion = () => {
  if (!detailsRef.value || !props.isToggleable) return

  const wasOpen = detailsRef.value.open
  if (needsPolyfill.value) {
    // For Safari, manually handle the toggle
    if (wasOpen) {
      // Closing
      isAccordionOpen.value = false
      animateClose()
    } else {
      // Opening
      isAccordionOpen.value = true
      detailsRef.value.open = true
      animateOpen()
    }
  } else {
    // For other browsers, let the native behavior work
    detailsRef.value.open = !wasOpen
    isAccordionOpen.value = !wasOpen
  }

  const eventName = `custom.accordion${props.gaOptions?.group === 'checkout' ? '2' : ''}.${isAccordionOpen.value ? 'open' : 'close'}`

  // SAFETY: both branches of the template build a name in the `custom.accordion*` family, which is
  // part of DataLayerEventName; the template literal just widens it back to string.
  pushEventToDataLayer(props?.eventName || (eventName as DataLayerEventName), {
    name: props.title,
    ...props.gaOptions,
    ...accordionActionParam(),
    info: {
      element: detailsRef.value?.querySelector('summary')?.innerHTML,
      ...props.gaOptions?.info,
    },
  })
  emit('toggle', isAccordionOpen.value)
}

const animateOpen = async () => {
  if (!detailsRef.value) return

  await nextTick()

  const content = detailsRef.value.querySelector('.content')
  if (!content) return

  contentHeight.value = content.scrollHeight
  accordionHeight.value = content.scrollHeight + 'px'
  contentReady.value = true
}

const animateClose = async () => {
  if (!detailsRef.value) return

  const content = detailsRef.value.querySelector('.content')
  if (!content) return

  accordionHeight.value = '0px'

  // Wait for animation to complete before fully closing
  setTimeout(() => {
    if (!isAccordionOpen.value && detailsRef.value) {
      detailsRef.value.open = false
      contentReady.value = false
    }
  }, 300) // match transition duration
}

// Listen for native toggle events (for non-Safari browsers)
const handleToggle = (event: ToggleEvent & { detail?: { doNotTrack?: boolean } }) => {
  if (needsPolyfill.value) return // We handle this manually for Safari

  const toggleEvent = event
  isAccordionOpen.value = toggleEvent.newState === 'open'

  if (isAccordionOpen.value) {
    contentReady.value = true
  } else {
    setTimeout(() => {
      contentReady.value = false
    }, 300) // Wait for animation to complete
  }

  // Avoid tracking the initial state on mount as this is not an actual user interaction but the initial state
  if (!isOpenOnMount.value && !event?.detail?.doNotTrack) {
    let eventName = `custom.accordion${props.gaOptions?.group === 'checkout' ? '2' : ''}.`
    eventName += isAccordionOpen.value ? 'open' : 'close'

    // SAFETY: as above, the assembled name is always a `custom.accordion*` member.
    pushEventToDataLayer(props?.eventName || (eventName as DataLayerEventName), {
      name: props.title,
      ...props.gaOptions,
      ...accordionActionParam(),
      info: {
        // SAFETY: the handler is bound to the <details> toggle event, so the target is that element.
        element: (event.target as HTMLElement)?.innerHTML,
        ...props.gaOptions?.info,
      },
    })
    emit('toggle', isAccordionOpen.value)
  }
  if (isOpenOnMount.value) {
    isOpenOnMount.value = false
  }
}

watch(
  () => props.open,
  () => {
    detailsRef.value?.dispatchEvent(new CustomEvent('toggle', { bubbles: true, detail: { doNotTrack: true } }))
  },
)

onMounted(() => {
  needsPolyfill.value = isSafari16OrBelow()
  if (props.open) {
    contentReady.value = true
    isAccordionOpen.value = true

    // For Safari, ensure content is visible
    if (needsPolyfill.value && detailsRef.value) {
      detailsRef.value.open = true
      nextTick(() => {
        animateOpen()
      })
    }
  }

  if (needsPolyfill.value && detailsRef.value) {
    const summary = detailsRef.value.querySelector('summary')
    // Prevent default toggle behavior for Safari and use our custom implementation
    summary?.addEventListener('click', (e) => {
      e.preventDefault()
      toggleAccordion()
    })
  }
})

onBeforeUnmount(() => {
  if (needsPolyfill.value && detailsRef.value) {
    const summary = detailsRef.value.querySelector('summary')
    summary?.removeEventListener('click', toggleAccordion)
  }
})
</script>

<template>
  <div
    class="accordion"
    :class="{
      secondary: variant === 'secondary',
      'not-toggleable': !isToggleable,
    }"
  >
    <details
      ref="detailsRef"
      :data-testid="panelTestId"
      :open="open || isAccordionOpen"
      :name="name"
      @toggle="handleToggle"
    >
      <summary :data-testid="testId">
        <slot name="title">{{ title }}</slot>
      </summary>
      <div
        v-show="contentReady || open || isAccordionOpen"
        class="content"
        :style="needsPolyfill ? { maxHeight: accordionHeight } : {}"
      >
        <slot></slot>
      </div>
    </details>
  </div>
</template>

<style lang="postcss">
.accordion {
  details {
    transition: var(--transition-1);
    overflow: hidden;
  }

  .content {
    padding-bottom: var(--space-50);
    transition: max-height 0.3s ease-out;
    overflow: hidden;
  }

  summary::-webkit-details-marker {
    display: none;
  }

  summary {
    cursor: pointer;
    list-style: none;
    font-size: var(--text-small);
    font-weight: bold;
    padding-block: var(--space-50);
    position: relative;
    transition: var(--transition-1);
    user-select: none;
  }

  details:has(summary:hover) {
    border-color: var(--ui-neutral-30);
  }

  details[open] > summary {
    &:after {
      transform: rotate(180deg);
    }
  }

  summary:after {
    content: '';
    background: url('~/assets/sprite/svg/symbol-arrow-down.svg') no-repeat;
    width: 1.5rem;
    height: 1.5rem;
    display: block;
    position: absolute;
    top: 0;
    right: var(--space-30);
    translate: 0% 50%;
    transition: var(--transition-1);
  }

  details {
    border-bottom: 1px solid var(--ui-neutral-20);
  }

  &.not-toggleable {
    @media (--tablet) {
      summary {
        cursor: default; /* Remove pointer cursor when not togglable on tablet/desktop */
      }
      summary:after {
        display: none; /* Hide chevron when not togglable on tablet/desktop */
      }
    }
  }
}

.accordion.secondary {
  details[open] > summary {
    &:after {
      background: url('~/assets/sprite/svg/minus.svg') no-repeat;
      transform: none;
    }
  }
  summary:after {
    background: url('~/assets/sprite/svg/plus.svg') no-repeat;
  }
}
</style>
